import { toLineBreak, toParagraphContent } from '@principle-theorem/editor';
import {
  MarketingCampaign,
  MarketingCampaignStep,
  Practice,
  RecurrencePattern,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  MarketingCampaignType,
  TemplateScope,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  FactTables,
  patientBirthdayCampaignReportTemplate,
} from '@principle-theorem/reporting';
import {
  ALL_REGIONS,
  asReffable,
  CustomRecurrenceFrequency,
  DocumentReference,
} from '@principle-theorem/shared';
import { CampaignTemplateHelpers } from './campaign-template-helpers';
import { IMarketingCampaignTemplate } from './marketing-campaign-template';
import { MarketingCampaignTemplateFactory } from './marketing-campaign-template-factory';

export function getBirthdayCampaignTemplate(
  practiceRef: DocumentReference<IPractice>
): IMarketingCampaignTemplate {
  return {
    name: 'Birthday Campaign',
    description: `Celebrate your patients' birthdays with personalized emails and SMS messages to make them feel special on their big day.`,
    regions: ALL_REGIONS,
    campaign: MarketingCampaign.init({
      brandRef: Practice.brandRef(asReffable(practiceRef)),
      practiceRef,
      name: 'Birthday Campaign',
      type: MarketingCampaignType.GeneralNotice,
      scope: TemplateScope.Patient,
      entryResetRules: {
        multipleEntry: true,
        reentryScope: TemplateScope.Patient,
        maxEntryCount: undefined,
        frequencyCount: 1,
        frequencyType: CustomRecurrenceFrequency.Yearly,
      },
      recurrencePattern: RecurrencePattern.init({
        customFrequencyType: CustomRecurrenceFrequency.Daily,
      }),
      recurrenceRunTime: '08:37',
    }),
    report: patientBirthdayCampaignReportTemplate(
      FactTables.patientEvent.latestEvent
    ),
    steps: [
      MarketingCampaignStep.init({
        name: 'Happy Birthday SMS',
        config: MarketingCampaignTemplateFactory.automationSMS(
          [
            toParagraphContent(
              [
                'Happy Birthday {{ patientFirstName }}! 🎂🎈 May your day be full of laughter, joy, and of course... a big healthy smile 😁.',
                'From all of us at {{ practiceName }}',
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 1,
            unit: TimingUnit.Minutes,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasMobile(true),
          ])
        ),
      }),
      MarketingCampaignStep.init({
        name: 'Happy Birthday Email (Backup)',
        config: MarketingCampaignTemplateFactory.automationEmail(
          'Happy Birthday from {{ practiceName }} 🎉',
          [
            toParagraphContent(
              [
                'Hi {{ patientFirstName }},',
                toLineBreak(),
                'The whole team at {{ practiceName }} wishes you a very Happy Birthday! 🥳',
                'We hope your day is filled with smiles, laughter, and all the things that make you happiest.',
                toLineBreak(),
                'Thank you for being part of our dental family - we love helping keep your smile bright all year round.',
                toLineBreak(),
                'Warm wishes,',
                'The {{ practiceName }} Team 🦷✨',
                CampaignTemplateHelpers.highlight('[INSERT LOGO]'),
              ],
              true
            ),
          ],
          {
            direction: TimingDirection.After,
            amount: 3,
            unit: TimingUnit.Hours,
          },
          MarketingCampaignTemplateFactory.conditions([
            MarketingCampaignTemplateFactory.conditionHasEmail(true),
            MarketingCampaignTemplateFactory.conditionHasMobile(false),
          ])
        ),
      }),
    ],
  };
}
